import React, { useState, useEffect } from 'react';
import api from '../api';
import { Search, X, Check, AlertTriangle, Plus, UserPlus } from 'lucide-react';

// Odabir ljudi za postojeći zadatak (uređivanje zadatka): članovi (grupe zadatka na vrhu) i gosti,
// upozorenje ako je član taj dan već na drugom zadatku, „Obavezno” samo admin.
// onStage: samo vrati odabir (primjenjuje se na „Spremi promjene”); bez onStage sprema odmah (onDone).

function Avatar({ name, size = 30, color }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const colors = ['#0984e3','#6c5ce7','#00b894','#e17055','#fd79a8','#fdcb6e','#74b9ff','#a29bfe'];
  const bg = color || colors[(name || '').charCodeAt(0) % colors.length];
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: bg, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, fontWeight: 600, flexShrink: 0, letterSpacing: '-0.5px' }}>
      {initials}
    </div>
  );
}

export default function AddPeoplePanel({ task, user, allUsers, allGroups, allGuests, setAllGuests, taskGuests, volunteers, listRef, onDone, onStage, excludeEmails, excludeGuestIds }) {
  const [tab, setTab] = useState('members');
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState({});        // email -> { is_mandatory }
  const [pickedGuests, setPickedGuests] = useState([]);
  const [busyMap, setBusyMap] = useState({});      // email -> [{ title, start_date }]
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const isAdmin = user.role === 'admin';

  useEffect(() => {
    api.get(`/tasks/${task.id}/busy`).then(res => {
      const m = {};
      for (const r of res.data) (m[r.email] = m[r.email] || []).push(r);
      setBusyMap(m);
    }).catch(() => {});
  }, [task.id]);

  const onTask = new Set([...(task.assignments || []).filter(a => a.status !== 'declined').map(a => a.email), ...(excludeEmails || [])]);
  const volunteerSet = new Set(volunteers.filter(v => !v.is_assigned && v.volunteer_status !== 'declined').map(v => v.email));
  const needle = q.trim().toLowerCase();
  const matches = (name, email) => !needle || (name || '').toLowerCase().includes(needle) || (email || '').toLowerCase().includes(needle);
  const candidates = allUsers.filter(u => !onTask.has(u.email) && matches(u.full_name, u.email));

  // Prvo članovi grupa ovog zadatka, zatim svi ostali.
  const sections = [];
  const placed = new Set();
  for (const tg of task.groups || []) {
    const g = allGroups.find(x => x.id === tg.id);
    if (!g) continue;
    const memberEmails = new Set((g.members || []).map(m => m.email));
    const people = candidates.filter(u => memberEmails.has(u.email) && !placed.has(u.email));
    people.forEach(u => placed.add(u.email));
    if (people.length) sections.push({ key: `g${g.id}`, label: `Grupa ${g.name}`, people });
  }
  const rest = candidates.filter(u => !placed.has(u.email));
  if (rest.length) sections.push({ key: 'rest', label: sections.length ? 'Ostali članovi' : 'Članovi', people: rest });

  const guestCandidates = allGuests.filter(g => !taskGuests.some(tg => tg.id === g.id) && !excludeGuestIds?.has(g.id) && matches(g.full_name));
  const canCreateGuest = isAdmin && needle && !allGuests.some(g => g.full_name.toLowerCase() === needle);

  const toggle = (email) => setPicked(p => {
    const n = { ...p };
    if (n[email]) delete n[email]; else n[email] = { is_mandatory: false };
    return n;
  });
  const toggleMandatory = (email) => setPicked(p => ({ ...p, [email]: { is_mandatory: !p[email]?.is_mandatory } }));
  const toggleGuest = (id) => setPickedGuests(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

  const createGuest = async () => {
    try {
      const res = await api.post('/guests', { full_name: q.trim() });
      setAllGuests(prev => [...prev, res.data]);
      setPickedGuests(p => [...p, res.data.id]);
      setQ('');
    } catch (e) { setErr(e.response?.data?.error || 'Greška pri dodavanju gosta.'); }
  };

  const pickedEmails = Object.keys(picked);
  const total = pickedEmails.length + pickedGuests.length;
  const invitesOnly = pickedGuests.length === 0 && pickedEmails.every(e => !volunteerSet.has(e));

  const submit = async () => {
    if (!total || saving) return;
    // Uređivanje zadatka: samo zabilježi, primjenjuje se na „Spremi”.
    if (onStage) {
      onStage({
        members: pickedEmails.map(email => ({
          email,
          is_mandatory: volunteerSet.has(email) ? false : !!picked[email].is_mandatory,
          full_name: allUsers.find(u => u.email === email)?.full_name || email,
          isVol: volunteerSet.has(email),
        })),
        guests: pickedGuests.map(id => allGuests.find(g => g.id === id)).filter(Boolean),
      });
      return;
    }
    setSaving(true);
    setErr('');
    try {
      for (const email of pickedEmails) {
        await api.post(`/tasks/${task.id}/assignments`, { email, is_mandatory: !!picked[email].is_mandatory, action: 'add' });
      }
      for (const guest_id of pickedGuests) {
        await api.post(`/tasks/${task.id}/guests`, { guest_id, action: 'add' });
      }
      await onDone();
    } catch (e) {
      setErr(e.response?.data?.error || 'Greška pri dodavanju.');
      setSaving(false);
    }
  };

  const fmtBusy = (list) => list.map(b => {
    const d = new Date(b.start_date);
    return `${b.title} ${d.toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' })}`;
  }).join(', ');

  return (
    <>
      <div className="tdm-add-top">
        {allGuests.length > 0 || isAdmin ? (
          <div className="tdm-add-seg">
            <button type="button" className={tab === 'members' ? 'on' : ''} onClick={() => setTab('members')}>Članovi{pickedEmails.length ? ` · ${pickedEmails.length}` : ''}</button>
            <button type="button" className={tab === 'guests' ? 'on' : ''} onClick={() => setTab('guests')}>Gosti{pickedGuests.length ? ` · ${pickedGuests.length}` : ''}</button>
          </div>
        ) : null}
        <div className="tdm-add-search">
          <Search size={16} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={tab === 'members' ? 'Pretraži članove' : 'Pretraži goste'} />
          {q && <button type="button" onClick={() => setQ('')} aria-label="Očisti"><X size={15} /></button>}
        </div>
      </div>

      <div className="tdm-participants-list tdm-add-list" ref={listRef}>
        {tab === 'members' ? (
          sections.length === 0 ? (
            <div className="tdm-add-empty">{needle ? 'Nema rezultata.' : 'Svi članovi su već na zadatku.'}</div>
          ) : sections.map(sec => (
            <div key={sec.key}>
              <div className="tdm-psec-h">{sec.label} · {sec.people.length}</div>
              {sec.people.map(u => {
                const on = !!picked[u.email];
                const isVol = volunteerSet.has(u.email);
                const busy = busyMap[u.email];
                return (
                  <div key={u.email} className="tdm-prow tdm-add-row" onClick={() => toggle(u.email)}>
                    <span className={`tdm-cb${on ? ' on' : ''}`}>{on && <Check size={14} strokeWidth={3} />}</span>
                    <Avatar name={u.full_name || u.email} size={30} />
                    <span className="tdm-prow-name">
                      {u.full_name || u.email}{u.email === user.email && <span className="tdm-prow-you"> · ti</span>}
                      {isVol && <span className="tdm-prow-sub" style={{ color: '#3a6ea3' }}>javio/la se — odmah sudjeluje</span>}
                      {busy && <span className="tdm-prow-sub" style={{ color: '#a8781f' }}>isti dan: {fmtBusy(busy)}</span>}
                    </span>
                    {on && !isVol && isAdmin && (
                      <button
                        type="button"
                        className={`tdm-mand${picked[u.email].is_mandatory ? ' on' : ''}`}
                        onClick={e => { e.stopPropagation(); toggleMandatory(u.email); }}
                        title="Obavezno sudjelovanje — osoba ne može odbiti poziv"
                      >
                        <AlertTriangle size={12} /> Obavezno
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        ) : (
          <>
            {guestCandidates.length === 0 && !canCreateGuest && (
              <div className="tdm-add-empty">{needle ? 'Nema rezultata.' : 'Nema gostiju za dodati.'}</div>
            )}
            {guestCandidates.map(g => {
              const on = pickedGuests.includes(g.id);
              return (
                <div key={g.id} className="tdm-prow tdm-add-row" onClick={() => toggleGuest(g.id)}>
                  <span className={`tdm-cb${on ? ' on' : ''}`}>{on && <Check size={14} strokeWidth={3} />}</span>
                  <Avatar name={g.full_name} size={30} color="#b2bec3" />
                  <span className="tdm-prow-name">{g.full_name}</span>
                </div>
              );
            })}
            {canCreateGuest && (
              <button type="button" className="tdm-add-newguest" onClick={createGuest}>
                <Plus size={15} /> Novi gost „{q.trim()}”
              </button>
            )}
          </>
        )}
      </div>

      <div className="tdm-add-bar">
        {err && <div className="tdm-add-err">{err}</div>}
        <button type="button" className="tdm-btn-flow tdm-btn-flow-primary" disabled={!total || saving} onClick={submit}>
          <UserPlus size={16} />
          {saving ? 'Dodajem…' : total === 0 ? 'Odaberi ljude' : `${invitesOnly ? 'Pozovi' : 'Dodaj'} (${total})`}
        </button>
      </div>
    </>
  );
}
